import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { MessageSquareQuote, MessageSquareText } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { useHistoryBack } from '@/hooks/useHistoryBack';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { STICKY_SIDE_PANEL_CLASS } from '@/styles/stickySidePanel';
import { feedbackDetailQueryOptions } from '../api/feedback.api';
import { FeedbackCompleteForm } from '../components/FeedbackCompleteForm';
import { FeedbackDetailHero } from '../components/FeedbackDetailHero';
import { FeedbackProgressTimeline } from '../components/FeedbackProgressTimeline';
import { FeedbackTextPanel } from '../components/FeedbackTextPanel';
import { useMarkFeedbackRead } from '../hooks/useMarkFeedbackRead';
import { isFeedbackCompleted } from '../utils/feedbackStatus';

const NO_COMMENT_TEXT = 'No comment was added.';
const NO_MESSAGE_TEXT = 'No message was written.';

interface FeedbackDetailPageProps {
  feedbackId: number;
}

export function FeedbackDetailPage({ feedbackId }: FeedbackDetailPageProps) {
  const navigate = useNavigate();
  const goBack = useHistoryBack(() => void navigate({ to: '/feedback/list' }));
  useRememberViewedRow('feedbacks', feedbackId);

  const { data: feedback, error, refetch } = useQuery(feedbackDetailQueryOptions(feedbackId));
  const { mutate: markFeedbackRead } = useMarkFeedbackRead();

  const isUnread = feedback?.readAt === null;
  useEffect(() => {
    if (isUnread) markFeedbackRead(feedbackId);
  }, [isUnread, feedbackId, markFeedbackRead]);

  const isLoading = feedback === undefined;
  const isCompleted = feedback !== undefined && isFeedbackCompleted(feedback.status);

  return (
    <div className="flex flex-col gap-page-gap">
      <PageHeader title="Feedback Details" leading={<BackButton onClick={goBack} />} />

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load this feedback"
          onRetry={() => void refetch()}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <FeedbackDetailHero feedback={feedback} />

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
            <div className="flex min-w-0 flex-col gap-4">
              <FeedbackTextPanel
                title="Message"
                icon={MessageSquareText}
                text={feedback?.message}
                emptyText={NO_MESSAGE_TEXT}
                isLoading={isLoading}
              />
              {isCompleted ? (
                <FeedbackTextPanel
                  title="Comment"
                  icon={MessageSquareQuote}
                  text={feedback.adminMessage}
                  emptyText={NO_COMMENT_TEXT}
                  isLoading={false}
                />
              ) : null}
              {feedback && !isCompleted ? <FeedbackCompleteForm feedbackId={feedbackId} /> : null}
            </div>

            <div className={STICKY_SIDE_PANEL_CLASS}>
              <FeedbackProgressTimeline feedback={feedback} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
